import * as React from 'react';
import { useSnackbar } from 'notistack';
import axios from '../../utils/axios';
import { Num } from './index';
import Modal, { BotaoSecundario, Campo, IconeOlho, classesEntrada } from './Modal';
import { cn } from '../../lib/utils';

/* ============================================================================
   Mudar a senha de desbloqueio do dispositivo - visual novo.

   Substitui pages/dashboard/dispositivos/components/modal/modal-change-password.tsx.

   ISTO MANDA COMANDO PARA O APARELHO. A senha de desbloqueio muda na hora, e
   quem estiver com o telefone na mao passa a precisar da nova. Se a senha for
   digitada errada aqui, o aparelho fica inacessivel para quem esta em campo -
   por isso o campo tem confirmacao de digitacao e a acao tem confirmacao final.

   Contrato, o mesmo de antes:
     PATCH /dispositivos/mudarSenha
       { deviceId, password, resetPasswordFlag, LOCK_NOW,
         DO_NOT_ASK_CREDENTIALS_ON_BOOT, dataCampo }

   As QUATRO flags do Android Management API sao enviadas exatamente com os mesmos
   nomes, e `resetPasswordFlag` continua indo junto (o antigo mandava os dois:
   a flag "principal" e os quatro booleanos).

   ---------------------------------------------------------------------------
   TRES DECISOES, todas por causa do risco:

   1. CONFIRMACAO DE SENHA (campo repetido). O modal antigo tinha um campo so.
      Senha de desbloqueio digitada errada nao da erro em lugar nenhum - ela e
      aceita, aplicada, e o aparelho fica inacessivel. Repetir e barato; destravar
      um telefone em campo nao e.

   2. `RESET_PASSWORD_FLAG_UNSPECIFIED` e `REQUIRE_ENTRY` SAIRAM da tela em
      29/09/2026 - as duas estao comentadas no backend e nao faziam nada. Ver o
      comentario na lista de flags, logo abaixo.

   3. A SENHA ATUAL continua sendo exibida, como no modal antigo (`device.password`
      vem na listagem). Nao e decisao minha esconder ou mostrar: o dado ja chega
      no payload da consulta, e omitir na tela nao o protege. Ver secao 6 do
      PENDENCIAS - a senha vir em texto claro na resposta e item para tratar no
      backend, nao aqui.
   ============================================================================ */

type Device = {
  deviceId?: string;
  nome?: string;
  password?: string;
  resetPasswordFlag?: string;
  softwareInfo_androidVersion?: string;
};

// Tipo explicito: com `as const` puro o TS estreita cada item e `alerta` passa a
// existir so no ultimo, quebrando o acesso no map.
const FLAGS: Array<{ id: string; rotulo: string; dica: string; alerta?: boolean }> = [
  {
    id: 'LOCK_NOW',
    rotulo: 'Bloquear ao trocar senha',
    dica: 'O aparelho bloqueia assim que receber a troca.',
  },
  {
    id: 'DO_NOT_ASK_CREDENTIALS_ON_BOOT',
    rotulo: 'Não pedir senha ao iniciar',
    dica: 'Não solicita credenciais do usuário na inicialização do dispositivo.',
  },
  /* DUAS OPCOES SAIRAM DAQUI (29/09/2026).

     Eram `REQUIRE_ENTRY` ("Bloquear alteracao de senha por outro admin") e
     `RESET_PASSWORD_FLAG_UNSPECIFIED` ("Valor nao especificado"). As duas
     estao COMENTADAS no backend (dispositivos.service.ts, montagem de
     `resetPasswordFlag`), entao marcar qualquer uma nao produzia efeito
     nenhum no aparelho.

     E nao era so nao funcionar: marcando SO uma delas, a lista de flags do
     backend ficava vazia e a resposta era "Nenhum flag de reset de senha foi
     especificado!". Ou seja, a pessoa marcava uma caixa e o sistema respondia
     que ela nao marcou nada - erro que parece defeito de sistema e e, na
     verdade, uma opcao que nunca existiu.

     `RESET_PASSWORD_FLAG_UNSPECIFIED` nao deveria estar aqui de todo jeito: e
     o valor ZERO do enum do Google, o "nao informado". Oferecer isso como
     escolha e pedir que alguem escolha "nenhuma escolha".

     Para voltar com o REQUIRE_ENTRY - que e flag legitima do Android - basta
     descomentar a linha correspondente no backend. E decisao de produto, nao
     de tela: muda o que chega ao aparelho. */
];

export default function ModalMudarSenha({
  aberto,
  device,
  onFechar,
  onSalvo,
}: {
  aberto: boolean;
  device: Device | null;
  onFechar: () => void;
  onSalvo?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const [senha, setSenha] = React.useState('');
  const [confirmar, setConfirmar] = React.useState('');
  const [mostrar, setMostrar] = React.useState(false);
  const [flags, setFlags] = React.useState<Record<string, boolean>>({ LOCK_NOW: true });
  const [confirmando, setConfirmando] = React.useState(false);
  const [salvando, setSalvando] = React.useState(false);

  React.useEffect(() => {
    if (!aberto) return;
    setSenha('');
    setConfirmar('');
    setMostrar(false);
    setConfirmando(false);
    /* LOCK_NOW marcado por padrao (30/09/2026). A API exige ao menos uma
       opcao ("Nenhum flag de reset de senha foi especificado!"); desde que
       REQUIRE_ENTRY saiu da tela (nao se aplica a aparelho gerenciado), as duas
       caixas abriam desmarcadas e trocar a senha sem mexer nelas dava erro.
       Bloquear ao trocar e o que o envio ja assumia (`resetPasswordFlag ?? 'LOCK_NOW'`). */
    setFlags({ LOCK_NOW: true });
  }, [aberto, device?.deviceId]);

  // Android 14+ exige 6 caracteres. A checagem existia no modal antigo mas estava
  // COMENTADA - aqui ela avisa sem bloquear, porque a versao do aparelho pode
  // chegar vazia da consulta e travar o fluxo por dado ausente seria pior.
  const versao = Number(device?.softwareInfo_androidVersion);
  const exigeSeis = Number.isFinite(versao) && versao >= 14;
  const curtaDemais = exigeSeis && senha.length > 0 && senha.length < 6;

  const senhaOk = senha.length > 0 && senha === confirmar;

  async function salvar() {
    if (!device?.deviceId || !senhaOk || salvando) return;
    setSalvando(true);
    try {
      // Mesmas entradas de auditoria do modal antigo.
      const dataCampo = JSON.stringify([
        {
          campo: 'tipo',
          antigo: '-',
          novo: `Bloquear ao trocar senha : ${flags.LOCK_NOW ? 'Marcado' : 'Não Marcado'}`,
        },
        {
          campo: 'tipo',
          antigo: '-',
          novo: `Não pedir senha ao iniciar : ${flags.DO_NOT_ASK_CREDENTIALS_ON_BOOT ? 'Marcado' : 'Não Marcado'}`,
        },
        { campo: 'senha', antigo: device.password || ' - ', novo: senha },
      ]);

      await axios.patch('/dispositivos/mudarSenha', {
        deviceId: device.deviceId,
        password: senha,
        resetPasswordFlag: device.resetPasswordFlag ?? 'LOCK_NOW',
        LOCK_NOW: Boolean(flags.LOCK_NOW),
        DO_NOT_ASK_CREDENTIALS_ON_BOOT: Boolean(flags.DO_NOT_ASK_CREDENTIALS_ON_BOOT),
        dataCampo,
      });

      // O Google ENFILEIRA o comando: aparelho offline so recebe quando voltar a ficar online.
      enqueueSnackbar('Comando enviado. A senha muda quando o aparelho receber (ele precisa estar online; o comando expira em cerca de 3 min).', { variant: 'success' });
      setConfirmando(false);
      onSalvo?.();
      onFechar();
    } catch (err: any) {
      const e = err?.response?.data;
      const msg = e?.errors?.length ? e.errors[0]?.message : e?.message;
      enqueueSnackbar(msg || 'Ops ocorreu um erro no sistema.', {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <Modal
        aberto={aberto && !confirmando}
        titulo="Mudar a senha do dispositivo"
        descricao={device ? `${device.nome ?? ''} · ${device.deviceId ?? ''}` : undefined}
        icone="🔑"
        largura="max-w-[560px]"
        onFechar={onFechar}
        rodape={
          <>
            <BotaoSecundario onClick={onFechar} disabled={salvando}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={() => setConfirmando(true)}
              disabled={!senhaOk || salvando}
              className="h-9 rounded-lg bg-warn px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              Mudar senha
            </button>
          </>
        }
      >
        {device?.password && (
          <div className="rounded-lg border border-line bg-surface-2 p-3">
            <span className="block text-[11px] font-bold uppercase tracking-[0.07em] text-faint">
              Senha atual
            </span>
            <Num className="block text-[12.5px] font-semibold">{device.password}</Num>
          </div>
        )}

        <Campo rotulo="Senha nova" obrigatorio dica={exigeSeis ? 'Android 14 ou superior: mínimo 6 caracteres.' : undefined}>
          <div className="flex gap-2">
            <input
              type={mostrar ? 'text' : 'password'}
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              autoComplete="new-password"
              className={cn(classesEntrada, 'tabular-nums', curtaDemais && 'border-warn')}
            />
            <button
              type="button"
              onClick={() => setMostrar((v) => !v)}
              aria-label={mostrar ? 'Ocultar senha' : 'Mostrar senha'}
              aria-pressed={mostrar}
              title={mostrar ? 'Ocultar senha' : 'Mostrar senha'}
              className="grid h-9 w-9 flex-none place-items-center rounded-lg border border-line text-muted-foreground hover:bg-soft hover:text-ink"
            >
              <IconeOlho fechado={mostrar} />
            </button>
          </div>
          {curtaDemais && (
            <span className="mt-1 block text-[11px] text-warn">
              Este aparelho é Android {device?.softwareInfo_androidVersion} e o sistema pode recusar senha com
              menos de 6 caracteres.
            </span>
          )}
        </Campo>

        {/* Repetir a senha: digitada errada, ela e aceita, aplicada, e o aparelho
            fica inacessivel para quem esta em campo. */}
        <Campo rotulo="Repita a senha nova" obrigatorio>
          <input
            type={mostrar ? 'text' : 'password'}
            value={confirmar}
            onChange={(e) => setConfirmar(e.target.value)}
            autoComplete="new-password"
            className={cn(
              classesEntrada,
              'tabular-nums',
              confirmar.length > 0 && confirmar !== senha && 'border-danger'
            )}
          />
          {confirmar.length > 0 && confirmar !== senha && (
            <span className="mt-1 block text-[11px] text-danger">As duas senhas não conferem.</span>
          )}
        </Campo>

        <div>
          <span className="mb-1.5 block text-[12px] font-semibold">Opções do comando</span>
          <div className="space-y-1.5">
            {FLAGS.map((f) => (
              <label
                key={f.id}
                className={cn(
                  'flex cursor-pointer items-start gap-2.5 rounded-lg border p-2.5',
                  f.alerta && flags[f.id] ? 'border-warn bg-warn-soft' : 'border-line'
                )}
              >
                <input
                  type="checkbox"
                  checked={Boolean(flags[f.id])}
                  onChange={(e) => setFlags((v) => ({ ...v, [f.id]: e.target.checked }))}
                  className="mt-0.5 h-3.5 w-3.5 accent-[hsl(var(--primary))]"
                />
                <span>
                  <span className={cn('block text-[12px] font-semibold', f.alerta && 'text-warn')}>
                    {f.rotulo}
                  </span>
                  <span className="block text-[11px] text-muted-foreground">{f.dica}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      </Modal>

      <Modal
        aberto={confirmando}
        titulo="Confirmar troca de senha?"
        descricao="A senha muda assim que o aparelho receber o comando (precisa estar online). Quem estiver com ele em mãos passa a precisar da nova."
        icone="!"
        largura="max-w-[500px]"
        onFechar={() => setConfirmando(false)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setConfirmando(false)} disabled={salvando}>
              Voltar
            </BotaoSecundario>
            <button
              type="button"
              onClick={salvar}
              disabled={salvando}
              className="h-9 rounded-lg bg-warn px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {salvando ? 'Enviando...' : 'Confirmar'}
            </button>
          </>
        }
      >
        <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px]">
          <span className="block font-semibold">{device?.nome || '—'}</span>
          <Num className="block text-[11px] text-muted-foreground">{device?.deviceId || '—'}</Num>
          <div className="mt-2">
            <span className="text-muted-foreground">Senha nova: </span>
            <Num className="font-bold text-ink">{mostrar ? senha : '•'.repeat(senha.length)}</Num>
          </div>
        </div>

      </Modal>
    </>
  );
}
